import {
  countHeaderRows,
  ISummaryTableColumn,
  ISummaryTableRow
} from '@opengov/reporting-embeds/src/tables/components/Table';

/**
 * Boundary [start, end) of leaf columns that should be rendered in one PDF chunk.
 */
export type ColumnChunkBoundary = readonly [number, number];

export const respanHeaders = (
  headerColumns: ISummaryTableColumn[],
  start: number,
  end: number
): ISummaryTableColumn[] => {
  let currentId = 0;

  return headerColumns.reduce((result, element) => {
    const { colSpan } = element;

    if (currentId >= end) {
      return result;
    }

    if (currentId + colSpan > start) {
      const cut = Math.max(start - currentId, 0);
      result.push({
        id: String(currentId + cut - start),
        value: element.value,
        colSpan: Math.min(colSpan - cut, end - currentId)
      });
    }

    currentId += colSpan;

    return result;
  }, []);
};

export const splitTable = (data: ISummaryTableRow[], start: number, end: number): ISummaryTableRow[] => {
  const headerRowCount = countHeaderRows(data);
  const leafHeaderIndex = headerRowCount - 1;
  const leafHeaderRow = data[leafHeaderIndex];

  const splitStart = start < 0 ? 0 : start;
  const splitEnd = end > leafHeaderRow.col.length ? leafHeaderRow.col.length : end;

  // Only the leaf row holds one cell per column and can be sliced directly. Every row above it is
  // colSpan-merged, so it has to be re-spanned onto the chunk — including the intermediate
  // organize-by levels, which a fixed [headers, subHeaders] split dropped into the body.
  const splitHeaderRows = data.slice(0, headerRowCount).map((headerRow, index) => {
    if (index === leafHeaderIndex) {
      return { ...headerRow, col: headerRow.col.slice(splitStart, splitEnd) };
    }
    return { ...headerRow, col: respanHeaders(headerRow.col, splitStart, splitEnd) };
  });

  const updatedData = [
    ...splitHeaderRows,
    ...data.slice(headerRowCount).map((rowGroup) => ({
      ...rowGroup,
      col: rowGroup.col.slice(splitStart, splitEnd),
      rows: splitSubRowGroups(rowGroup.rows, splitStart, splitEnd)
    }))
  ];

  return updatedData;
};

const splitSubRowGroups = (subRowGroups: ISummaryTableRow[], start: number, end: number): ISummaryTableRow[] => {
  return subRowGroups?.map((subRowGroup) => {
    return {
      ...subRowGroup,
      col: subRowGroup.col.slice(start, end),
      rows: splitSubRowGroups(subRowGroup.rows, start, end)
    };
  });
};

export const divideTablePerColumns = (
  data: ISummaryTableRow[],
  start: number,
  splitSize: number,
  options: { keepColumnGroupsTogether?: boolean } = {}
): ISummaryTableRow[][] => {
  // A table with no header rows has no leaf row to size the chunks from, and `splitTable` would
  // read `data[-1]` and throw. Guarded alongside the empty case rather than left to the boundary
  // arithmetic, so the invariant `splitTable` relies on is stated where it is established.
  if (!data.length || countHeaderRows(data) < 1) return [];

  // Leaf-column count comes from the LEAF header row. Reading it from `data[1]` picked an
  // intermediate organize-by level once a column group had more than one, and its colSpan-merged
  // cell count truncated the export to that many columns.
  const totalElements = data[countHeaderRows(data) - 1]?.col?.length ?? 0;
  if (totalElements === 0) return [];

  const boundaries = options.keepColumnGroupsTogether
    ? computeGroupAwareChunkBoundaries(data[0]?.col ?? [], totalElements, splitSize, start)
    : computeFixedSizeChunkBoundaries(start, totalElements, splitSize);

  return boundaries.map(([s, e]) => splitTable(data, s, e));
};

/**
 * Compute [start, end) leaf-column ranges by hard-slicing every `splitSize` columns,
 * starting from `start`. This is the legacy behavior preserved for callers that do not
 * pass `keepColumnGroupsTogether`.
 */
function computeFixedSizeChunkBoundaries(
  start: number,
  totalElements: number,
  splitSize: number
): ColumnChunkBoundary[] {
  const boundaries: ColumnChunkBoundary[] = [];
  let cursor = start;
  while (cursor < totalElements) {
    const next = Math.min(cursor + splitSize, totalElements);
    boundaries.push([cursor, next] as const);
    cursor = next;
  }
  return boundaries;
}

/**
 * Compute [start, end) leaf-column ranges that respect column-group boundaries while
 * still capping each chunk at `splitSize` leaf columns.
 *
 * Rules (mirrors BUD-15786 acceptance criteria):
 *  - If the next group fits in the remaining capacity of the current chunk, include it whole.
 *  - If it does not fit and the current chunk is non-empty, close the chunk and start a new one.
 *  - If a single group is larger than `splitSize`, slice it across consecutive chunks of
 *    `splitSize` columns each. No page break is enforced; this matches "page break is not
 *    required for the column group overflow" from the AC.
 *
 * `headerColumns` is the column-group header row (data[0].col); each entry's `colSpan`
 * is the leaf-column count of that group.
 */
function computeGroupAwareChunkBoundaries(
  headerColumns: ISummaryTableColumn[],
  totalElements: number,
  splitSize: number,
  start: number
): ColumnChunkBoundary[] {
  if (splitSize <= 0) return [];

  const groupRanges = expandHeaderToLeafRanges(headerColumns, totalElements, start);
  if (groupRanges.length === 0) {
    return computeFixedSizeChunkBoundaries(start, totalElements, splitSize);
  }

  const state: PackState = { chunks: [], chunkStart: groupRanges[0][0], chunkSize: 0 };

  for (const [groupStart, groupEnd] of groupRanges) {
    packGroupIntoChunks(state, groupStart, groupEnd, splitSize);
  }

  flushPendingChunk(state);
  return state.chunks;
}

type PackState = {
  chunks: ColumnChunkBoundary[];
  chunkStart: number;
  chunkSize: number;
};

/**
 * Convert the group-header `colSpan`s into absolute leaf-column ranges, clipped to the
 * `[start, totalElements)` window. Groups that fall entirely outside the window are dropped.
 */
function expandHeaderToLeafRanges(
  headerColumns: ISummaryTableColumn[],
  totalElements: number,
  start: number
): ColumnChunkBoundary[] {
  const ranges: ColumnChunkBoundary[] = [];
  let cursor = 0;
  for (const group of headerColumns) {
    const span = Math.max(0, group.colSpan ?? 0);
    if (span === 0) continue;
    const groupStart = cursor;
    const groupEnd = cursor + span;
    cursor = groupEnd;
    if (groupEnd <= start) continue;
    if (groupStart >= totalElements) break;
    ranges.push([Math.max(groupStart, start), Math.min(groupEnd, totalElements)] as const);
  }
  return ranges;
}

/**
 * Append a single group's leaf-column range to the current packing state.
 *
 * Either:
 *  - the group fits in the current chunk's remaining capacity (extend chunk), or
 *  - the chunk is closed and the group is sliced across new chunks of `splitSize` columns.
 */
function packGroupIntoChunks(state: PackState, groupStart: number, groupEnd: number, splitSize: number): void {
  const span = groupEnd - groupStart;
  if (span <= 0) return;

  const fitsInCurrentChunk = state.chunkSize + span <= splitSize;
  if (fitsInCurrentChunk) {
    if (state.chunkSize === 0) state.chunkStart = groupStart;
    state.chunkSize += span;
    return;
  }

  flushPendingChunk(state);
  emitOversizedGroupChunks(state, groupStart, groupEnd, splitSize);
}

/**
 * Slice a group that is wider than `splitSize` (or that does not fit in the previous chunk)
 * into one or more chunks. The trailing partial chunk is left open in `state` so the next
 * smaller group can still be packed into it.
 */
function emitOversizedGroupChunks(state: PackState, groupStart: number, groupEnd: number, splitSize: number): void {
  let cursor = groupStart;
  while (groupEnd - cursor > splitSize) {
    const next = cursor + splitSize;
    state.chunks.push([cursor, next] as const);
    cursor = next;
  }
  state.chunkStart = cursor;
  state.chunkSize = groupEnd - cursor;
}

function flushPendingChunk(state: PackState): void {
  if (state.chunkSize === 0) return;
  state.chunks.push([state.chunkStart, state.chunkStart + state.chunkSize] as const);
  state.chunkSize = 0;
}
